import {Record, List, Map} from 'immutable';
import moment from 'moment-timezone';

import BehaviorsCollection from 'web-app/behaviors/behaviors-collection';
import {getMappedBehaviors} from 'web-app/react/entities/observations/helpers';
import * as FileHelpers from 'web-app/react/entities/files/helpers';
import * as ImageUtil from 'web-app/util/image';

import Recipient from '../recipients/model';

const Draft = Record({
    postId: '',
    createdAt: null,
    observedAt: null,
    body: '',
    richTextBody: '',
    nextStep: '',
    from: '',
    recipients: List(),
    statements: List(),
    areaRefinements: List(),
    isObservation: false,
    video: null,
    files: List(),
    behaviors: new BehaviorsCollection(),
    postBehaviors: new BehaviorsCollection(),
    variant: '',
    images: List(),
    disableFeedback: false,
    activities: null,
    sentAt: null,
});

const Statement = Record({
    id: '',
    body: '',
});

const RemarkStatement = Record({
    statement: new Statement(),
    refinement: '',
});

const Area = Record({
    parentId: '',
    id: '',
});

const AreaRefinement = Record({
    area: new Area(),
    refinement: '',
    note: '',
});

const remarkStatementFromAPI = remarkStatement =>
    new RemarkStatement({
        statement: new Statement(remarkStatement.statement),
        refinement: remarkStatement.refinement,
    });

const areaRefinementFromAPI = areaRefinement =>
    new AreaRefinement({
        area: new Area(areaRefinement.area),
        refinement: areaRefinement.refinement,
        note: areaRefinement.note,
    });

const imageRecordToGallery = imageRecord => {
    const {item} = imageRecord;

    if (item.secret) {
        return {
            ...item,
            imageId: item.id,
            prefix: `${item.secret.prefix}/${item.secret.key}`,
            key: `${item.secret.path}?expires=${item.secret.expires}`,
        };
    }

    if (Map.isMap(item)) {
        return item.toJS();
    }

    return item;
};

function fromAPI(data, isObservation = false) {
    const {recipients, files, images, video, ...draft} = data;

    const imagesList = List(images).map(image => {
        return FileHelpers.imageToFileRecord(image).set(
            'tinyThumbnailUrl',
            ImageUtil.getImageUrl(image, 100, 100, {forceDimensions: true}),
        );
    });

    const filesList = List(files).map(FileHelpers.fileToFileRecord);

    const videoRecord = FileHelpers.videoToFileRecord(video);

    if (isObservation) {
        // This is needed since backend doesn't currently send behaviors with a payload,
        // but instead on the root behavior object
        const mappedBehaviors = getMappedBehaviors(data);

        return new Draft({
            ...draft,
            behaviors: new BehaviorsCollection(mappedBehaviors),
            postId: draft.id,
            body: draft.remark.body,
            observedAt: draft.remark.date,
            nextStep: draft.nextStep ? draft.nextStep.body : '',
            isObservation,
            statements: isObservation ? List(draft.remark.statements).map(remarkStatementFromAPI) : List(),
            areaRefinements: isObservation ? List(draft.remark.areas).map(areaRefinementFromAPI) : List(),
            createdAt: moment(draft.status.createdAt),
            images: imagesList,
            recipients: List(draft.children).map(
                child =>
                    new Recipient.record({
                        id: child.id,
                        title: child.name,
                    }),
            ),
            files: filesList,
            variant: draft.variant,
            video: videoRecord,
        });
    }

    const recipientsList = List(recipients).map(Recipient.fromAPI);

    return new Draft({
        ...draft,
        isObservation,
        createdAt: draft.createdAt ? moment(draft.createdAt) : moment(),
        images: imagesList,
        recipients: recipientsList,
        files: filesList,
        video: videoRecord,
        postBehaviors: new BehaviorsCollection(draft.postBehaviors),
    });
}

function toAPI(draft) {
    return draft.toJS();
}

export default {
    record: Draft,
    fromAPI,
    imageToFileRecord: FileHelpers.imageToFileRecord, // Deprecated, use directly
    fileToFileRecord: FileHelpers.fileToFileRecord, // Deprecated, use directly
    videoToFileRecord: FileHelpers.videoToFileRecord, // Deprecated, use directly
    imageRecordToGallery,
    toAPI,
};
